import React, { useState, useEffect } from 'react'
import { FileDown, Search, Filter, ShoppingBag, IndianRupee, DollarSign } from 'lucide-react'
import PageIdentifier from '../shared/PageIdentifier'
import SCREEN_IDS from '../../utils/screenIds'
import * as XLSX from 'xlsx'
import BillDetailModal from '../shared/BillDetailModal'

const SoldItems = () => {
  const [items, setItems] = useState([])
  const [summary, setSummary] = useState(null)
  const [categoryBreakdown, setCategoryBreakdown] = useState([])
  const [paymentBreakdown, setPaymentBreakdown] = useState([])
  const [selectedBill, setSelectedBill] = useState(null)
  const [billLoading, setBillLoading] = useState(false)
  const [loading, setLoading] = useState(true)
  const [categories, setCategories] = useState([])
  const [vendors, setVendors] = useState([])

  const today = new Date()
  const firstOfMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`
  const todayStr = today.toISOString().split('T')[0]

  const [filters, setFilters] = useState({
    category: 'all',
    vendor: 'all',
    purity: 'all',
    currency: 'all',
    dateFrom: firstOfMonth,
    dateTo: todayStr,
    search: ''
  })

  useEffect(() => { fetchFilterOptions() }, [])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchReport() }, [filters])

  const fetchFilterOptions = async () => {
    try {
      const [catRes, venRes] = await Promise.all([
        fetch('/api/categories', { credentials: 'include' }),
        fetch('/api/vendors',    { credentials: 'include' })
      ])
      const catData = await catRes.json()
      const venData = await venRes.json()
      if (Array.isArray(catData)) setCategories(catData.filter(c => c.parent_id !== null))
      if (Array.isArray(venData)) setVendors(venData)
    } catch (err) {
      console.error('Error fetching filter options:', err)
    }
  }

  const fetchReport = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      Object.entries(filters).forEach(([k, v]) => { if (v && v !== 'all') params.append(k, v) })
      const res  = await fetch(`/api/reports/sold-items?${params}`, { credentials: 'include' })
      const data = await res.json()
      if (data.success) {
        setItems(data.data.items)
        setSummary(data.data.summary)
        setCategoryBreakdown(data.data.categoryBreakdown)
        setPaymentBreakdown(data.data.paymentBreakdown || [])
      }
    } catch (err) {
      console.error('Error fetching sold items report:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (key, value) => setFilters(prev => ({ ...prev, [key]: value }))

  const handleClearFilters = () => setFilters({
    category: 'all', vendor: 'all', purity: 'all', currency: 'all',
    dateFrom: firstOfMonth, dateTo: todayStr, search: ''
  })

  const openBill = async (billNumber) => {
    if (!billNumber) return
    setBillLoading(true)
    try {
      const res = await fetch(`/api/billings?search=${encodeURIComponent(billNumber)}&limit=1`, { credentials: 'include' })
      const data = await res.json()
      const bill = data.success && data.data && data.data[0]
      if (bill) setSelectedBill(bill)
    } catch (err) {
      console.error('Failed to load bill:', err)
    } finally {
      setBillLoading(false)
    }
  }

  const handleExport = () => {
    if (!items.length) return
    const rows = items.map(item => ({
      'Bill No.':      item.sold_bill_number || '-',
      'Sold Date':     item.sold_date ? new Date(item.sold_date).toLocaleDateString('en-IN') : '-',
      'Item Code':     item.code,
      'Category':      item.category || '-',
      'Purity':        item.gold_purity ? `${item.gold_purity}KT` : '-',
      'Gross Wt (g)':  parseFloat(item.gross_weight || 0).toFixed(3),
      'Net Wt (g)':    parseFloat(item.net_weight   || 0).toFixed(3),
      'Fine Net Wt (g)': item.fine_net_weight != null ? parseFloat(item.fine_net_weight).toFixed(3) : '',
      'Customer':      item.customer_name  || '-',
      'Phone':         item.customer_phone || '-',
      'Currency':      item.currency || '-',
      'Sold Price':    parseFloat(item.sold_price || 0).toFixed(2),
      'Vendor':        item.vendor_code || '-',
      'Payment Mode':  (item.payments || []).map(p => paymentTypeLabel(p.type)).join('; ') || '-',
      'Payment Ref #': (item.payments || []).map(p => p.reference).filter(Boolean).join('; ') || '-',
      'Payment Status': item.payment_status || '-'
    }))
    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.json_to_sheet(rows)
    XLSX.utils.book_append_sheet(wb, ws, 'Sold Items')
    XLSX.writeFile(wb, `Sold_Items_Report_${todayStr}.xlsx`)
  }

  const fmtINR = (amount) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 })
      .format(Math.ceil(parseFloat(amount) || 0))

  const fmtWt = (w) => parseFloat(w || 0).toFixed(3)

  const paymentTypeLabels = {
    cash: 'Cash', card: 'Card', upi: 'UPI', cheque: 'Cheque',
    bank_transfer: 'Bank Transfer', old_gold: 'Old Gold Exchange',
    gold_coin: 'Gold Coin Exchange', gold_scheme: 'Gold Scheme', elavon: 'Elavon'
  }
  const paymentTypeLabel = (t) => paymentTypeLabels[t] || t

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageIdentifier pageId={SCREEN_IDS.REPORTS.SOLD_ITEMS_REPORT} pageName="Sold Items Report" />

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <ShoppingBag className="text-blue-600" size={28} />
            Sold Items Report
          </h1>
          <p className="text-sm text-gray-500 mt-1">All items sold from inventory</p>
        </div>
        <button
          onClick={handleExport}
          disabled={!items.length}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium"
        >
          <FileDown size={18} />
          Export to Excel
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow col-span-1">
            <p className="text-xs text-gray-500">Total Sold</p>
            <p className="text-2xl font-bold text-gray-800">{summary.total_sold}</p>
            <p className="text-xs text-gray-400 mt-1">INR: {summary.inr_count} · USD: {summary.usd_count}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow col-span-1">
            <p className="text-xs text-gray-500">Gross Weight</p>
            <p className="text-xl font-bold text-amber-700">{fmtWt(summary.total_gross_weight)}g</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow col-span-1">
            <p className="text-xs text-gray-500">Net Weight</p>
            <p className="text-xl font-bold text-amber-600">{fmtWt(summary.total_net_weight)}g</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow col-span-1 md:col-span-3">
            <div className="flex items-center gap-2">
              <IndianRupee size={20} className="text-green-600" />
              <p className="text-xs text-gray-500">Total Sold Value</p>
            </div>
            <p className="text-2xl font-bold text-green-700">{fmtINR(summary.total_sold_value)}</p>
            <p className="text-xs text-gray-400 mt-1">Sum of sold prices across all filtered items</p>
          </div>
        </div>
      )}

      {/* Category Breakdown */}
      {categoryBreakdown.length > 0 && (
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <h2 className="text-base font-semibold text-gray-800 mb-3">Sales by Category</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {categoryBreakdown.map((cat, i) => (
              <div key={i} className="border border-gray-100 p-3 rounded-lg bg-gray-50">
                <p className="text-sm font-medium text-gray-700 truncate">{cat.category}</p>
                <p className="text-xl font-bold text-gray-900">{cat.count}</p>
                <p className="text-xs text-green-700 font-medium mt-1">{fmtINR(cat.total_value)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payment Mode Breakdown */}
      {paymentBreakdown.length > 0 && (
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <h2 className="text-base font-semibold text-gray-800 mb-3">Payments by Mode</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {paymentBreakdown.map((p, i) => (
              <div key={i} className="border border-gray-100 p-3 rounded-lg bg-gray-50">
                <p className="text-sm font-medium text-gray-700 truncate">{paymentTypeLabel(p.payment_type)}</p>
                <p className="text-xl font-bold text-gray-900">{p.count}</p>
                <p className="text-xs text-green-700 font-medium mt-1">{fmtINR(p.total_amount)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter size={18} className="text-gray-500" />
          <h2 className="text-base font-semibold text-gray-700">Filters</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">

          {/* Search */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                value={filters.search}
                onChange={e => handleFilterChange('search', e.target.value)}
                placeholder="Item code, customer, bill…"
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Date From */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Sold From</label>
            <input type="date" value={filters.dateFrom}
              onChange={e => handleFilterChange('dateFrom', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
          </div>

          {/* Date To */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Sold To</label>
            <input type="date" value={filters.dateTo}
              onChange={e => handleFilterChange('dateTo', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
          </div>

          {/* Category */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
            <select value={filters.category} onChange={e => handleFilterChange('category', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none">
              <option value="all">All Categories</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          {/* Vendor */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Vendor</label>
            <select value={filters.vendor} onChange={e => handleFilterChange('vendor', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none">
              <option value="all">All Vendors</option>
              {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>

          {/* Purity */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Gold Purity</label>
            <select value={filters.purity} onChange={e => handleFilterChange('purity', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none">
              <option value="all">All Purities</option>
              <option value="14">14 KT</option>
              <option value="18">18 KT</option>
              <option value="22">22 KT</option>
              <option value="24">24 KT</option>
            </select>
          </div>

          {/* Currency */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Currency</label>
            <select value={filters.currency} onChange={e => handleFilterChange('currency', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none">
              <option value="all">All Currencies</option>
              <option value="INR">INR</option>
              <option value="USD">USD</option>
            </select>
          </div>

          {/* Clear */}
          <div className="flex items-end">
            <button onClick={handleClearFilters}
              className="w-full px-4 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50">
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              <p className="mt-2 text-gray-500 text-sm">Loading sold items…</p>
            </div>
          ) : items.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {[
                    'Sold Date', 'Bill No.', 'Item Code', 'Category',
                    'Purity', 'Gross Wt', 'Net Wt', 'Fine Net Wt', 'Stones',
                    'Customer', 'Phone', 'Currency', 'Sold Price', 'Vendor', 'Payment'
                  ].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map(item => (
                  <tr
                    key={item.id}
                    className="hover:bg-blue-50 transition-colors cursor-pointer"
                    onClick={() => openBill(item.sold_bill_number)}
                    title={item.sold_bill_number ? `View bill ${item.sold_bill_number}` : undefined}
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                      {item.sold_date ? new Date(item.sold_date).toLocaleDateString('en-IN') : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap font-medium text-blue-700">
                      {item.sold_bill_number || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap font-semibold text-gray-900">
                      {item.code}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                      {item.category || '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-semibold rounded-full">
                        {item.gold_purity ? `${item.gold_purity}KT` : '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700">{fmtWt(item.gross_weight)}g</td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700">{fmtWt(item.net_weight)}g</td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700">{item.fine_net_weight != null ? `${fmtWt(item.fine_net_weight)}g` : '-'}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-xs">
                      {item.stone_count > 0
                        ? <span>{item.stone_count} · {fmtWt(item.stone_weight_grams)}g</span>
                        : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-800 font-medium">
                      {item.customer_name || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-500">
                      {item.customer_phone || '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {item.currency === 'USD'
                        ? <span className="flex items-center gap-0.5 text-green-700 font-semibold"><DollarSign size={12}/>USD</span>
                        : <span className="flex items-center gap-0.5 text-orange-700 font-semibold"><IndianRupee size={12}/>INR</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap font-bold text-green-700">
                      {item.sold_price
                        ? (item.currency === 'USD'
                          ? `$${parseFloat(item.sold_price).toFixed(2)}`
                          : fmtINR(item.sold_price))
                        : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                      {item.vendor_code || '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {item.payments && item.payments.length > 0 ? (
                        <div className="flex flex-col gap-0.5">
                          {item.payments.map((p, i) => (
                            <span key={i} className="text-xs text-gray-700" title={p.reference ? `Ref: ${p.reference}` : undefined}>
                              {paymentTypeLabel(p.type)} · {fmtINR(p.amount)}
                              {p.reference && <span className="text-gray-400"> ({p.reference})</span>}
                            </span>
                          ))}
                          {item.payment_status && item.payment_status !== 'paid' && (
                            <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-800 text-[10px] font-semibold rounded w-fit">
                              {item.payment_status}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-14">
              <ShoppingBag className="mx-auto h-12 w-12 text-gray-300" />
              <p className="mt-3 text-gray-500 font-medium">No sold items found</p>
              <p className="text-xs text-gray-400 mt-1">Try adjusting the date range or filters</p>
            </div>
          )}
        </div>
      </div>

      {items.length > 0 && (
        <div className="mt-3 text-xs text-gray-500 text-right">
          Showing {items.length} {items.length === 1 ? 'item' : 'items'} · Click a row to view full invoice
        </div>
      )}

      {billLoading && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-xl px-8 py-6 shadow-xl text-sm text-gray-600 flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
            Loading invoice…
          </div>
        </div>
      )}

      {selectedBill && (
        <BillDetailModal
          bill={selectedBill}
          onClose={() => setSelectedBill(null)}
        />
      )}
    </div>
  )
}

export default SoldItems
